/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */

import { Note, TunePadAudio } from "../../audio";

// Quantize button
// add rests in the history


/**
 * This class keeps a buffer of notes played and released to auto generate python code from an instrument UI
 */
export class NoteRecorder {

    public static QUANT = 0.25;

    private audio : TunePadAudio;

    private groups : NoteGroup[] = [];

    get current() { return this.groups[this.groups.length - 1]; }

    constructor() {
        this.audio = TunePadAudio.init();
    }

    noteOn(note : number | Note) {
        if (this.groups.length === 0 || this.current.closed) {
            this.groups.push(new NoteGroup(this.audio));
        }
        this.current.noteOn(note, this.audio.contextTime);
    }

    noteOff(note : number | Note) {
        if (this.groups.length > 0) {
            this.current.noteOff(note, this.audio.contextTime);
        }
    }

    get currentHint() {
        if (this.groups.length === 0) {
            return '';
        } else if (this.current.empty) {
            return '';
        } else {
            return this.current.getHint(this.audio.bpm);
        }
    }

    get currentNoteNumbers() : number[] {
        if (this.groups.length === 0) {
            return [];
        } else {
            return this.current.notes.map(nr => nr.note.note);
        }
    }

    /** Get full history of notes played with rests inserted */
    getHistory(quant : boolean) : string {
        let result = '';
        let last : NoteGroup | undefined = undefined;
        for (const group of this.groups) {
            const delta = group.onset - (last ? last.release : group.onset);
            if (delta > 4) {
                result += `\n# Long rest (${quantize(delta)} beats)\n\n`;
            }
            else if (delta > 1/8) {
                result += `rest(${toFixedString(quant ? quantize(delta) : delta)})\n`;
            }
            result += group.getHint(this.audio.bpm, quant) + '\n';
            last = group;
        }
        return result;
    }

    clear() {
        this.groups = [];
    }
}


class NoteGroup {
    audio : TunePadAudio;
    notes : NoteRecord[] = [];

    get open() { return !this.closed; }
    get closed() { return this.notes.every(n => n.closed ); }
    get empty() { return this.notes.length === 0; }

    get onset() { return this.notes.reduce((min, n) => Math.min(min, n.onset), this.notes[0].onset); }
    get release() { return this.notes.reduce((max, n) => Math.max(max, n.release), this.notes[0].release); }
    get minNote() { return this.notes.reduce((min, n) => Math.min(min, n.note.note), this.notes[0].note.note); }
    get durationSeconds() { return (this.notes.length > 0)? this.release - this.onset : 0; }


    constructor(audio : TunePadAudio) {
        this.audio = audio;
    }

    noteOn(n : number | Note, onset : number) {
        let note = (n instanceof Note) ? n : new Note(n);
        const existing = this.notes.find(n => n.note.note === note.note);
        if (existing) {
            existing.release = 0;
        } else {
            this.notes.push(new NoteRecord(note, onset));
        }
        //if any other notes have been released, remove them
        this.notes = this.notes
            .filter(n => n.open)
            .sort((a, b) => a.note.note - b.note.note);
    }

    noteOff(n : number | Note, release : number) {
        let note = (n instanceof Note) ? n : new Note(n);
        const existing = this.notes.find(n => n.note.note === note.note);
        if (existing) {
            existing.release = release;
        }
    }

    getHint(bpm : number, quant = true) {

        const vals = this.minNote >= 16 ?
            this.notes.map(n => this.audio.key.getNoteVariableName(n.note)).join(', ') :
            this.notes.map(n => n.note.note).join(', ');
        const len = this.notes.length;
        const arg = (len > 1) ? `[ ${vals} ]` : (len > 0) ? `${vals}` : 'None';
        if (this.open) {
            return `play(${arg})`;
        } else {
            const beats = this.formatBeats(bpm, quant);
            return `play(${arg}, beats = ${beats})`
        }
    }

    private formatBeats(bpm : number, quant = true) {
        let d = this.durationSeconds * bpm / 60;
        if (quant) d = quantize(d);
        return toFixedString(d);
    }
}


class NoteRecord {

    /** note onset in seconds (will get converted to beats) */
    onset : number = -1;

    /** note release in seconds */
    release : number = 0;

    /** notes being played */
    note : Note;

    /** is this note still being played */
    get open() { return this.release <= 0; }
    get closed() { return this.release > 0; }


    constructor(note : Note, onset : number) {
        this.note = note;
        this.onset = onset;
        this.release = 0;
    }
}

import styles from './mcc.module.css' with { type: 'css' }
import html from './mcc.module.html';

/**
 * Dialog that shows a history of notes played
 */
export class NoteHistoryDialog {

    private root : ShadowRoot;
    private mcc : NoteRecorder;
    private overlay : HTMLDivElement;
    static quant : boolean = true;



    constructor(mcc : NoteRecorder) {
        this.mcc = mcc;
        this.overlay = document.createElement('div');
        this.overlay.classList.add('context-menu-overlay', 'show');
        this.overlay.addEventListener('pointerdown', e => { this.hide(); });
        
        this.root = this.overlay.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
        this.root.querySelector('.close-button')?.addEventListener('click', e => { this.hide(); });
        this.root.querySelector('#clear-history')?.addEventListener('click', e => {
            this.mcc.clear();
            this.setHTML('#recordings', '');
            this.hide();
        });
        this.root.querySelector('#clipboard-copy')?.addEventListener('click', e => {
            navigator.clipboard.writeText(this.mcc.getHistory(NoteHistoryDialog.quant));
            this.hide();
        });
        this.root.querySelector('.dialog')?.addEventListener('pointerdown', e => e.stopImmediatePropagation());
        this.setHTML('#recordings', this.mcc.getHistory(NoteHistoryDialog.quant));

        this.root.querySelector('#quantize')?.addEventListener('click', e => {
            if (NoteHistoryDialog.quant) {
                NoteHistoryDialog.quant = false;
                this.setHTML('#recordings', mcc.getHistory(false));
                this.root.querySelector('#quantize tunepad-icon')?.setAttribute('icon', '');
            } else {
                NoteHistoryDialog.quant = true;
                this.setHTML('#recordings', mcc.getHistory(true));
                this.root.querySelector('#quantize tunepad-icon')?.setAttribute('icon', 'checkmark');
            }
        });
        this.root.querySelector('#quantize tunepad-icon')?.setAttribute('icon', NoteHistoryDialog.quant ? 'checkmark' : '');
    }

    show() {
        document.body.append(this.overlay);
    }

    hide() {
        this.overlay.classList.remove('show');
        setTimeout(() => { this.overlay.remove() }, 175);
    }

    private setHTML(selector : string, html : string) {
        const el = this.root.querySelector(selector);
        if (el) el.innerHTML = html;
    }
}


function quantize(beats : number) : number {
    return Math.round(beats / NoteRecorder.QUANT) * NoteRecorder.QUANT;
}

function toFixedString(d : number, zeroPad : number = 1/8) : string {
    let beats = d.toFixed(3);
    for (let i=0; i<3; i++) {
        if (beats.endsWith('0')) beats = beats.substring(0, beats.length - 1);
    }
    if (beats.endsWith('.')) beats = beats.substring(0, beats.length - 1);
    if (beats === '0') beats = `${zeroPad}`;
    return beats;
}